import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    TextField,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Tooltip,
} from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

interface CloudNicehashConfigData {
    id: string;
    name: string;
    initialHashBuffer: number;
    timeWindowMinutes: number;
    efficiencyWindowMinutes: number;
    maxCap: number;
}

const emptyForm: CloudNicehashConfigData = {
    id: '',
    name: '',
    initialHashBuffer: 0,
    timeWindowMinutes: 360,
    efficiencyWindowMinutes: 0,
    maxCap: 0,
};

function CloudOrderSetting() {
    const [configs, setConfigs] = useState<CloudNicehashConfigData[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const [formData, setFormData] = useState<CloudNicehashConfigData>(emptyForm);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchConfigs = () => {
        setFetching(true);
        axios
            .get(API_PATH + '/nicehash/cloud/order/config', getAxiosConfig())
            .then((res) => setConfigs(res.data))
            .catch(() => setSnackbar({ open: true, message: 'Failed to fetch configurations.', severity: 'error' }))
            .finally(() => setFetching(false));
    };

    useEffect(() => {
        fetchConfigs();
    }, []);

    const handleOpenAdd = () => {
        setFormData(emptyForm);
        setFormOpen(true);
    };

    const handleOpenEdit = (row: CloudNicehashConfigData) => {
        setFormData({ ...row });
        setFormOpen(true);
    };

    const handleClose = () => setFormOpen(false);

    const handleChange = (field: keyof CloudNicehashConfigData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const handleSave = () => {
        setLoading(true);
        axios
            .post(API_PATH + '/nicehash/cloud/order/config', formData, getAxiosConfig())
            .then(() => {
                setSnackbar({ open: true, message: 'Configuration saved successfully!', severity: 'success' });
                fetchConfigs();
                handleClose();
            })
            .catch(() => setSnackbar({ open: true, message: 'Failed to save configuration.', severity: 'error' }))
            .finally(() => setLoading(false));
    };

    return (
        <Box p={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h5" fontWeight="bold">
                    Cloud Order Setting
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={handleOpenAdd}>
                    Add New
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Initial Hash Buffer (%)</strong></TableCell>
                            <TableCell><strong>Time Window (min)</strong></TableCell>
                            <TableCell><strong>Efficiency Window (min)</strong></TableCell>
                            <TableCell><strong>Max Cap (%)</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {fetching ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : configs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No configurations found. Click "Add New" to create one.
                                </TableCell>
                            </TableRow>
                        ) : (
                            configs.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.name}</TableCell>
                                    <TableCell>{row.initialHashBuffer}</TableCell>
                                    <TableCell>{row.timeWindowMinutes}</TableCell>
                                    <TableCell>{row.efficiencyWindowMinutes || 'Default'}</TableCell>
                                    <TableCell>{row.maxCap}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add / Edit Modal */}
            <Modal open={formOpen} onClose={handleClose}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 440,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {formData.id ? 'Edit Configuration' : 'Add Configuration'}
                    </Typography>

                    <TextField
                        fullWidth
                        label="Name"
                        value={formData.name}
                        onChange={handleChange('name')}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Initial Hash Buffer (%)"
                        value={formData.initialHashBuffer}
                        onChange={handleChange('initialHashBuffer')}
                        margin="normal"
                        type="number"
                        inputProps={{ step: 'any' }}
                    />
                    <TextField
                        fullWidth
                        label="Deficiency Window (minutes)"
                        value={formData.timeWindowMinutes}
                        onChange={handleChange('timeWindowMinutes')}
                        margin="normal"
                        type="number"
                    />
                    <TextField
                        fullWidth
                        label="Efficiency Window (minutes) - 0 for default"
                        value={formData.efficiencyWindowMinutes}
                        onChange={handleChange('efficiencyWindowMinutes')}
                        margin="normal"
                        type="number"
                    />
                    <TextField
                        fullWidth
                        label="Max Cap (%)"
                        value={formData.maxCap}
                        onChange={handleChange('maxCap')}
                        margin="normal"
                        type="number"
                        inputProps={{ step: 'any' }}
                    />

                    <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} /> : null}
                        >
                            {loading ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity as any}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default CloudOrderSetting;
